import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Header } from '../../components/Header';
import { BottomNav } from '../../components/BottomNav';
import { IdCard, Plus, Search, Pencil, X, Check, AlertCircle, Phone } from 'lucide-react';
import { REGIMENES_FISCALES, USOS_CFDI } from '../../constants/catalogosSat';
import { fmtMoney } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FISCAL_VACIO = { rfc: '', razon_social: '', regimen_fiscal: '', uso_cfdi: '', codigo_postal: '', email: '' };

const tieneDatosFiscalesCompletos = (c) =>
  !!(c.rfc && c.razon_social && c.regimen_fiscal && c.uso_cfdi && c.codigo_postal);

export function AdminClientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [creando, setCreando] = useState(false);

  const [editando, setEditando] = useState(null);
  const [fiscalForm, setFiscalForm] = useState(FISCAL_VACIO);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { loadClientes(); }, []);

  const loadClientes = async () => {
    try {
      const { data } = await axios.get(`${API}/clientes/global`);
      setClientes((data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    } catch {
      setError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const clientesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter(c => c.nombre.toLowerCase().includes(q) || (c.rfc || '').toLowerCase().includes(q));
  }, [clientes, busqueda]);

  const handleCrear = async () => {
    if (!nuevoNombre.trim()) { setError('Ingresa el nombre del cliente'); return; }
    setError('');
    setCreando(true);
    try {
      await axios.post(`${API}/clientes`, { nombre: nuevoNombre.trim(), telefono: nuevoTelefono.trim() || null });
      setShowCreate(false);
      setNuevoNombre('');
      setNuevoTelefono('');
      await loadClientes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setCreando(false);
    }
  };

  const abrirEdicion = (cliente) => {
    setEditando(cliente);
    setFiscalForm({
      rfc: cliente.rfc || '',
      razon_social: cliente.razon_social || '',
      regimen_fiscal: cliente.regimen_fiscal || '',
      uso_cfdi: cliente.uso_cfdi || '',
      codigo_postal: cliente.codigo_postal || '',
      email: cliente.email || '',
    });
    setError('');
  };

  const handleGuardarFiscal = async () => {
    setError('');
    setGuardando(true);
    try {
      await axios.patch(`${API}/clientes/${editando.id}/fiscal`, fiscalForm);
      setEditando(null);
      setSuccess('Datos fiscales actualizados');
      await loadClientes();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar los datos fiscales');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-clientes-page">
      <Header title="Clientes" />

      <div className="page-container fade-in">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
            <p className="text-red-700 font-medium text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600 shrink-0" />
            <p className="text-green-700 font-medium text-sm">{success}</p>
          </div>
        )}

        <button
          onClick={() => { setShowCreate(true); setError(''); }}
          className="w-full h-16 bg-blue-600 text-white rounded-xl text-xl font-bold btn-press flex items-center justify-center gap-3 mb-6"
          data-testid="btn-add-cliente"
        >
          <Plus className="w-6 h-6" />
          Nuevo Cliente
        </button>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o RFC..."
            className="w-full h-12 pl-9 pr-4 border-2 border-zinc-200 rounded-xl text-sm bg-white"
            data-testid="input-buscar-cliente"
          />
        </div>

        <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <IdCard className="w-5 h-5" />
          Clientes ({clientesFiltrados.length})
        </h3>

        {clientesFiltrados.length === 0 ? (
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center text-zinc-400 text-sm">
            {clientes.length === 0 ? 'No hay clientes registrados.' : 'Sin resultados para tu búsqueda.'}
          </div>
        ) : (
          <div className="space-y-3">
            {clientesFiltrados.map(c => {
              const completos = tieneDatosFiscalesCompletos(c);
              return (
                <div key={c.id} className="bg-white border-2 border-zinc-200 rounded-xl p-4" data-testid={`cliente-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-lg leading-tight">{c.nombre}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${completos ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {completos ? 'Datos fiscales completos' : 'Sin datos fiscales'}
                        </span>
                      </div>
                      {c.telefono && (
                        <p className="flex items-center gap-1 text-zinc-400 text-xs mt-1">
                          <Phone className="w-3 h-3" />{c.telefono}
                        </p>
                      )}
                      {completos ? (
                        <p className="text-zinc-500 text-sm mt-1 truncate">{c.razon_social} · {c.rfc}</p>
                      ) : null}
                      <p className={`text-sm mt-1 font-medium ${c.saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {c.saldo < 0 ? `Debe ${fmtMoney(Math.abs(c.saldo))}` : 'Al corriente'}
                      </p>
                    </div>
                    <button
                      onClick={() => abrirEdicion(c)}
                      className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg shrink-0"
                      data-testid={`btn-edit-cliente-${c.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-600" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal: nuevo cliente */}
        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Nuevo Cliente</h2>
                <button onClick={() => setShowCreate(false)} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4 mb-6">
                <input type="text" value={nuevoNombre}
                  onChange={e => setNuevoNombre(e.target.value)}
                  placeholder="Nombre del cliente *"
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                  data-testid="input-cliente-nombre"
                  autoFocus
                />
                <input type="tel" value={nuevoTelefono}
                  onChange={e => setNuevoTelefono(e.target.value)}
                  placeholder="Teléfono (opcional)"
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                  data-testid="input-cliente-telefono"
                />
                <p className="text-xs text-zinc-400">Los datos fiscales (RFC, régimen, etc.) se agregan después, editando al cliente.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setShowCreate(false)} className="h-14 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleCrear} disabled={creando || !nuevoNombre.trim()}
                  className="h-14 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
                  data-testid="btn-create-cliente"
                >
                  {creando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Crear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: editar datos fiscales */}
        {editando && (
          <div className="modal-overlay" onClick={() => setEditando(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Datos fiscales</h2>
                <button onClick={() => setEditando(null)} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-4">{editando.nombre}</p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}
              <div className="space-y-3 mb-6">
                <input type="text" value={fiscalForm.rfc}
                  onChange={e => setFiscalForm({ ...fiscalForm, rfc: e.target.value.toUpperCase() })}
                  placeholder="RFC" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm font-mono"
                  data-testid="input-fiscal-rfc"
                />
                <input type="text" value={fiscalForm.razon_social}
                  onChange={e => setFiscalForm({ ...fiscalForm, razon_social: e.target.value })}
                  placeholder="Razón social / Nombre" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm"
                  data-testid="input-fiscal-razon"
                />
                <select value={fiscalForm.regimen_fiscal}
                  onChange={e => setFiscalForm({ ...fiscalForm, regimen_fiscal: e.target.value })}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white"
                  data-testid="input-fiscal-regimen"
                >
                  <option value="">Régimen fiscal</option>
                  {REGIMENES_FISCALES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <select value={fiscalForm.uso_cfdi}
                  onChange={e => setFiscalForm({ ...fiscalForm, uso_cfdi: e.target.value })}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white"
                  data-testid="input-fiscal-uso"
                >
                  <option value="">Uso de CFDI</option>
                  {USOS_CFDI.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
                <input type="text" value={fiscalForm.codigo_postal}
                  onChange={e => setFiscalForm({ ...fiscalForm, codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                  placeholder="Código postal" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm"
                  data-testid="input-fiscal-cp"
                />
                <input type="email" value={fiscalForm.email}
                  onChange={e => setFiscalForm({ ...fiscalForm, email: e.target.value })}
                  placeholder="Correo (opcional)" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm"
                  data-testid="input-fiscal-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setEditando(null)} className="h-12 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleGuardarFiscal} disabled={guardando}
                  className="h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-save-fiscal"
                >
                  {guardando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
